import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Timestamp,
  type Firestore,
  type DocumentReference,
  type CollectionReference,
  type FieldValue,
  type QueryConstraint,
} from 'firebase/firestore';
import {
  makeExecutionId,
  type ExecutionDoc,
  type ExecutionStatus,
  type ProjectDoc,
  type TaskDoc,
  type RunDoc,
  type MemberRolesMap,
  type NodePosition,
  type UserId,
  type Role,
  type ExecutionType,
  type BacklogConfig,
  type BacklogCacheDoc,
  type ActivityLogDoc,
  type WbsCatalogOption,
} from '@mct/shared';
import { extractIssueKeys } from './backlog';

/** Shorthand refs ------------------------------------------------------- */

export const projectsCol = (db: Firestore): CollectionReference =>
  collection(db, 'projects');

export const projectRef = (db: Firestore, pid: string): DocumentReference =>
  doc(db, 'projects', pid);

export const tasksCol = (db: Firestore, pid: string): CollectionReference =>
  collection(db, 'projects', pid, 'tasks');

export const taskRef = (
  db: Firestore,
  pid: string,
  tid: string,
): DocumentReference => doc(db, 'projects', pid, 'tasks', tid);

export const runsCol = (db: Firestore, pid: string): CollectionReference =>
  collection(db, 'projects', pid, 'runs');

export const runRef = (
  db: Firestore,
  pid: string,
  rid: string,
): DocumentReference => doc(db, 'projects', pid, 'runs', rid);

export const executionsCol = (
  db: Firestore,
  pid: string,
): CollectionReference => collection(db, 'projects', pid, 'executions');

export const executionRef = (
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
): DocumentReference =>
  doc(db, 'projects', pid, 'executions', makeExecutionId(runId, taskId));

export const backlogCacheCol = (
  db: Firestore,
  pid: string,
): CollectionReference => collection(db, 'projects', pid, 'backlogCache');

export const activityLogsCol = (
  db: Firestore,
  pid: string,
): CollectionReference => collection(db, 'projects', pid, 'activityLogs');

/** Member-fields helper (addendum §2) ---------------------------------- */

export interface MemberFieldsSnapshot {
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;
}

export function memberFieldsFromProject(
  project: Pick<ProjectDoc, 'memberUserIds' | 'memberRoles'>,
): MemberFieldsSnapshot {
  return {
    memberUserIds: [...project.memberUserIds],
    memberRoles: { ...project.memberRoles },
  };
}

/** Project CRUD -------------------------------------------------------- */

export interface CreateProjectInput {
  name: string;
  description: string;
  ownerUserId: UserId;
  ownerDisplayName: string;
  ownerEmail: string;
  ownerPhotoURL?: string | null;
}

export async function createProject(
  db: Firestore,
  input: CreateProjectInput,
): Promise<string> {
  const ref = doc(projectsCol(db));
  const now = serverTimestamp();
  const ownerMember = {
    role: 'owner' as Role,
    displayName: input.ownerDisplayName,
    email: input.ownerEmail,
    photoURL: input.ownerPhotoURL ?? null,
    addedAt: now,
  };
  await setDoc(ref, {
    projectId: ref.id,
    name: input.name,
    description: input.description,
    ownerUserId: input.ownerUserId,
    memberUserIds: [input.ownerUserId],
    members: { [input.ownerUserId]: ownerMember },
    memberRoles: { [input.ownerUserId]: 'owner' as Role },
    backlog: null,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateProject(
  db: Firestore,
  pid: string,
  patch: Partial<Pick<ProjectDoc, 'name' | 'description'>>,
): Promise<void> {
  await updateDoc(projectRef(db, pid), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function updateBacklogConfig(
  db: Firestore,
  pid: string,
  config: BacklogConfig | null,
): Promise<void> {
  await updateDoc(projectRef(db, pid), {
    backlog: config,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Persist the user-managed option lists for 領域 / ベンダー on the
 * project doc. Validates `field` at the call site — the caller passes
 * a merged `WbsCatalog`, not a single list.
 *
 * Post-canonicalization, both `area` and `vendor` are persisted as
 * `WbsCatalogOption[]` with `{ id, label, colorIndex? }`. The legacy
 * `string[]` form is no longer accepted — run
 * `pnpm -F @mct/functions migrate:area-dropLegacy --apply` to convert
 * any leftover docs.
 */
export async function updateWbsCatalog(
  db: Firestore,
  pid: string,
  catalog: {
    area: ReadonlyArray<WbsCatalogOption>;
    vendor: ReadonlyArray<WbsCatalogOption>;
  },
): Promise<void> {
  await updateDoc(projectRef(db, pid), {
    wbsCatalog: catalog,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Persist scheduled-function preferences (daily-summary email + Backlog
 * sync pause). These drive the per-project gating inside
 * `sendDailyNotifications` / `syncBacklogIssues` / `ingestBacklogActivity`.
 */
export async function updateProjectSchedules(
  db: Firestore,
  pid: string,
  patch: {
    notifications?: {
      enabled: boolean;
      dailyHour: number;
      notifyOnTaskComplete?: boolean;
    };
    backlogSync?: { enabled: boolean };
  },
): Promise<void> {
  const fields: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.notifications) fields.notifications = patch.notifications;
  if (patch.backlogSync) fields.backlogSync = patch.backlogSync;
  await updateDoc(projectRef(db, pid), fields);
}

/**
 * Update the per-project scheduledJob doc — admins-only at the rules
 * level, but the helper takes a generic projectId so it can be reused
 * by both the Settings UI and any admin-tooling that wants to script
 * cron edits. Patch shape only includes the user-editable knobs;
 * runner-managed fields (lastRunAt, lastStatus, etc.) are filtered out
 * server-side by the rules block (`fieldsUnchanged`).
 */
export async function updateScheduledJob(
  db: Firestore,
  pid: string,
  jobId: string,
  patch: { cronExpression?: string; enabled?: boolean },
): Promise<void> {
  const fields: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (typeof patch.cronExpression === 'string') {
    fields.cronExpression = patch.cronExpression.trim();
    // Reset nextRunAt so the next tick re-evaluates immediately. The
    // runner recomputes nextRunAt at end-of-run, but until the first
    // run after a cron edit we want the saved cron to take effect on
    // the very next heartbeat — null means "due now".
    fields.nextRunAt = null;
  }
  if (typeof patch.enabled === 'boolean') {
    fields.enabled = patch.enabled;
  }
  await updateDoc(
    doc(db, 'projects', pid, 'scheduledJobs', jobId),
    fields,
  );
}

export async function addMember(
  db: Firestore,
  project: ProjectDoc,
  member: {
    userId: UserId;
    role: Role;
    displayName: string;
    email: string;
    photoURL?: string | null;
  },
): Promise<void> {
  const now = serverTimestamp();
  const newMembers = {
    ...project.members,
    [member.userId]: {
      role: member.role,
      displayName: member.displayName,
      email: member.email,
      photoURL: member.photoURL ?? null,
      addedAt: now,
    },
  };
  const newRoles: MemberRolesMap = {
    ...project.memberRoles,
    [member.userId]: member.role,
  };
  const newIds = [
    ...new Set([...project.memberUserIds, member.userId]),
  ];
  await updateDoc(projectRef(db, project.projectId), {
    members: newMembers,
    memberRoles: newRoles,
    memberUserIds: newIds,
    updatedAt: now,
  });
}

export async function removeMember(
  db: Firestore,
  project: ProjectDoc,
  userId: UserId,
): Promise<void> {
  if (userId === project.ownerUserId) {
    throw new Error('オーナーは削除できません');
  }
  const { [userId]: _m, ...restMembers } = project.members;
  const { [userId]: _r, ...restRoles } = project.memberRoles;
  void _m;
  void _r;
  await updateDoc(projectRef(db, project.projectId), {
    members: restMembers,
    memberRoles: restRoles,
    memberUserIds: project.memberUserIds.filter((u) => u !== userId),
    updatedAt: serverTimestamp(),
  });
}

export async function changeMemberRole(
  db: Firestore,
  project: ProjectDoc,
  userId: UserId,
  role: Role,
): Promise<void> {
  if (userId === project.ownerUserId && role !== 'owner') {
    throw new Error('オーナーのロールは変更できません');
  }
  const existing = project.members[userId];
  if (!existing) throw new Error('メンバーが見つかりません');
  await updateDoc(projectRef(db, project.projectId), {
    [`members.${userId}.role`]: role,
    [`memberRoles.${userId}`]: role,
    updatedAt: serverTimestamp(),
  });
}

/** Task CRUD ----------------------------------------------------------- */

export interface CreateTaskInput {
  name: string;
  description?: string;
  category?: string;
  subcategory?: string | null;
  assigneeId?: string | null;
  executionType?: ExecutionType;
  estimatedMinutes?: number;
  dependsOn?: string[];
  position?: NodePosition;
}

export async function createTask(
  db: Firestore,
  project: ProjectDoc,
  uid: UserId,
  input: CreateTaskInput,
): Promise<string> {
  const ref = doc(tasksCol(db, project.projectId));
  const now = serverTimestamp();
  const payload = {
    taskId: ref.id,
    projectId: project.projectId,
    // Hand-created tasks have no source row; the xlsx re-import pipeline is
    // the only writer of a non-null `sourceTaskId`.
    sourceTaskId: null,
    name: input.name,
    description: input.description ?? '',
    category: input.category ?? '',
    // Hand-created tasks have no source 大項目, so subcategory is null
    // unless the caller supplies one explicitly.
    subcategory: input.subcategory ?? null,
    assigneeId: input.assigneeId ?? null,
    executionType: (input.executionType ?? 'manual') as ExecutionType,
    estimatedMinutes: input.estimatedMinutes ?? 0,
    dependsOn: input.dependsOn ?? [],
    position: input.position ?? { x: 0, y: 0 },
    cycleDetected: false,
    cycleMembers: [] as string[],
    applicableRunLabels: [] as string[],
    ...memberFieldsFromProject(project),
    createdAt: now,
    updatedAt: now,
    createdByUserId: uid,
    updatedByUserId: uid,
  };
  await setDoc(ref, payload);
  return ref.id;
}

export async function updateTask(
  db: Firestore,
  pid: string,
  tid: string,
  uid: UserId,
  patch: Partial<Omit<TaskDoc, 'taskId' | 'projectId' | 'createdAt' | 'createdByUserId'>>,
): Promise<void> {
  await updateDoc(taskRef(db, pid, tid), {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  });
}

export async function updateTaskPosition(
  db: Firestore,
  pid: string,
  tid: string,
  uid: UserId,
  position: NodePosition,
): Promise<void> {
  await updateDoc(taskRef(db, pid, tid), {
    position,
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  });
}

export async function deleteTask(
  db: Firestore,
  pid: string,
  tid: string,
): Promise<void> {
  await deleteDoc(taskRef(db, pid, tid));
}

/**
 * Re-create a previously-deleted task document at its original id from a
 * captured snapshot. Used by the Undo stack to reverse a `deleteTask`.
 *
 * Note: this restores only the task master record — the cascade that
 * removed its Execution rows on delete is not replayed here. The next
 * `generateExecutions` for the relevant run rebuilds those.
 */
export async function restoreTask(
  db: Firestore,
  task: TaskDoc,
): Promise<void> {
  await setDoc(taskRef(db, task.projectId, task.taskId), {
    ...task,
    updatedAt: serverTimestamp(),
  });
}

/** Run CRUD ------------------------------------------------------------ */

export interface CreateRunInput {
  name: string;
  order: number;
  plannedStartDate: string;
  plannedEndDate: string;
}

export async function createRun(
  db: Firestore,
  project: ProjectDoc,
  uid: UserId,
  input: CreateRunInput,
): Promise<string> {
  const ref = doc(runsCol(db, project.projectId));
  const now = serverTimestamp();
  await setDoc(ref, {
    runId: ref.id,
    projectId: project.projectId,
    name: input.name,
    order: input.order,
    plannedStartDate: input.plannedStartDate,
    plannedEndDate: input.plannedEndDate,
    actualStartDate: null,
    actualEndDate: null,
    status: 'planned',
    executionsGeneratedCount: 0,
    expectedExecutionsCount: 0,
    executionsReady: false,
    ...memberFieldsFromProject(project),
    createdAt: now,
    updatedAt: now,
    createdByUserId: uid,
  });
  return ref.id;
}

export async function updateRun(
  db: Firestore,
  pid: string,
  rid: string,
  patch: Partial<Pick<RunDoc, 'name' | 'plannedStartDate' | 'plannedEndDate' | 'actualStartDate' | 'actualEndDate' | 'status' | 'order'>>,
): Promise<void> {
  await updateDoc(runRef(db, pid, rid), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteRun(
  db: Firestore,
  pid: string,
  rid: string,
): Promise<void> {
  await deleteDoc(runRef(db, pid, rid));
}

/**
 * Swap the `order` field of two runs atomically. Used by the phase list's
 * up/down reorder buttons — a write batch keeps the two updates from
 * landing partially and leaving duplicate `order` values that would
 * destabilise the sort.
 */
export async function swapRunOrders(
  db: Firestore,
  pid: string,
  a: { runId: string; order: number },
  b: { runId: string; order: number },
): Promise<void> {
  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.update(runRef(db, pid, a.runId), { order: b.order, updatedAt: now });
  batch.update(runRef(db, pid, b.runId), { order: a.order, updatedAt: now });
  await batch.commit();
}

/** Execution updates (client cannot create; only mutate fields) --------- */

export async function updateExecution(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  patch: Partial<Pick<
    ExecutionDoc,
    | 'status'
    | 'actualAssigneeUserId'
    | 'startedAt'
    | 'completedAt'
    | 'handoverNotes'
    | 'successorNotes'
    | 'backlogIssueUrls'
  >>,
): Promise<void> {
  const payload: Record<string, unknown> = {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  };
  if (patch.backlogIssueUrls) {
    payload['backlogIssueKeys'] = extractIssueKeys(patch.backlogIssueUrls);
  }
  await updateDoc(executionRef(db, pid, runId, taskId), payload);
}

/**
 * Publish the current `successorNotes` draft as a "post" — stamps
 * `successorNotesPostedAt` / `successorNotesPostedByUserId` so downstream
 * tasks see the message and (later) the notification trigger can fire on
 * the timestamp change. The body in `notes` is also written so the user's
 * latest edit is what gets published (drafts auto-save separately via
 * `updateExecution`, but we still write here to avoid a race where the
 * post lands before the debounce flush).
 */
export async function postSuccessorNotes(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  notes: string,
): Promise<void> {
  await updateDoc(executionRef(db, pid, runId, taskId), {
    successorNotes: notes,
    successorNotesPostedAt: serverTimestamp(),
    successorNotesPostedByUserId: uid,
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  });
}

/**
 * Phase-actual time-tracking update — used by the migration-task detail
 * panel's "実績時間（フェーズ別）" section. Persists actual start / end
 * for the (run × task) intersection by mapping to the canonical
 * `startedAt` / `completedAt` fields on the Execution document (the
 * schema already carries one timestamp per phase via the deterministic
 * `{runId}_{taskId}` ID, so no new field is needed).
 *
 * Pass `null` to clear a previously-set value. Either side may be
 * omitted to leave that side untouched — supports partial inline edits
 * where the user picks a start time and the end is still pending.
 */
export async function setExecutionActualTime(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  patch: { actualStartAt?: Date | null; actualEndAt?: Date | null },
): Promise<void> {
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  };
  if ('actualStartAt' in patch) {
    payload['startedAt'] =
      patch.actualStartAt ? Timestamp.fromDate(patch.actualStartAt) : null;
  }
  if ('actualEndAt' in patch) {
    payload['completedAt'] =
      patch.actualEndAt ? Timestamp.fromDate(patch.actualEndAt) : null;
  }
  await updateDoc(executionRef(db, pid, runId, taskId), payload);
}

export async function setExecutionStatus(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  status: ExecutionStatus,
): Promise<void> {
  const patch: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  };
  if (status === 'done') patch['completedAt'] = serverTimestamp();
  if (status === 'in_progress') patch['startedAt'] = serverTimestamp();
  await updateDoc(executionRef(db, pid, runId, taskId), patch);
}

export async function addBacklogUrl(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  existingUrls: readonly string[],
  url: string,
): Promise<void> {
  const next = [...new Set([...existingUrls, url])];
  await updateDoc(executionRef(db, pid, runId, taskId), {
    backlogIssueUrls: arrayUnion(url),
    backlogIssueKeys: extractIssueKeys(next),
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  });
}

export async function removeBacklogUrl(
  db: Firestore,
  pid: string,
  runId: string,
  taskId: string,
  uid: UserId,
  existingUrls: readonly string[],
  url: string,
): Promise<void> {
  const next = existingUrls.filter((u) => u !== url);
  await updateDoc(executionRef(db, pid, runId, taskId), {
    backlogIssueUrls: arrayRemove(url),
    backlogIssueKeys: extractIssueKeys(next),
    updatedAt: serverTimestamp(),
    updatedByUserId: uid,
  });
}

/** Previous-run handover resolver (addendum §5) ------------------------ */

/**
 * Fetch the most recent execution with `runOrder < currentRunOrder` for the
 * same task. Returns null when the task has no prior run.
 */
export async function fetchPreviousHandover(
  db: Firestore,
  pid: string,
  taskId: string,
  currentRunOrder: number,
): Promise<{ handoverNotes: string; runId: string; runOrder: number } | null> {
  if (currentRunOrder <= 1) return null;
  const constraints: QueryConstraint[] = [
    where('taskId', '==', taskId),
    where('runOrder', '<', currentRunOrder),
    orderBy('runOrder', 'desc'),
    limit(1),
  ];
  const snap = await getDocs(query(executionsCol(db, pid), ...constraints));
  const first = snap.docs[0];
  if (!first) return null;
  const data = first.data() as ExecutionDoc;
  return {
    handoverNotes: data.handoverNotes,
    runId: data.runId,
    runOrder: data.runOrder,
  };
}

/** Lookups ------------------------------------------------------------- */

export async function getProject(
  db: Firestore,
  pid: string,
): Promise<ProjectDoc | null> {
  const snap = await getDoc(projectRef(db, pid));
  return snap.exists() ? (snap.data() as ProjectDoc) : null;
}

/** Misc server timestamp helper (so call sites don't import firestore directly). */
export function serverNow(): FieldValue {
  return serverTimestamp();
}

/** Adopt addDoc-style for collections that the UI is allowed to create.
 *  (Executions are intentionally NOT included — see addendum §1/§4.) */
export { addDoc };

/** Re-exports for convenience */
export {
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  query,
  where,
  orderBy,
  limit,
};

// Re-exports to reduce import sprawl in components.
export type { BacklogCacheDoc, ActivityLogDoc };
